import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";

import {
  Grid,
  Paper,
  Alert as MuiAlert,
  Box,
  Avatar as MuiAvatar,
  Typography as MuiTypography,
  TextField,
  InputAdornment,
} from "@mui/material";
import { SportsEsports, Search } from "@mui/icons-material";

import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axios";
import { CircularIndeterminate } from "../components/Progress";
import { spacing } from "@mui/system";

const Alert = styled(MuiAlert)(spacing);

const Wrapper = styled(Paper)`
  width: 480px;
  max-width: 90%;
  padding: ${(props) => props.theme.spacing(10)}
    ${(props) => props.theme.spacing(24)};

  ${(props) => props.theme.breakpoints.down("md")} {
    padding: ${(props) => props.theme.spacing(5)};
  }
`;

const Typography = styled(MuiTypography)(spacing);

const Centered = styled.div`
  width: 72px;
  text-align: center;
  cursor: pointer;
`;

const Avatar = styled(MuiAvatar)`
  display: inline-flex;
  height: 56px;
  width: 56px;
  margin-bottom: 8px;
  border-radius: unset;
`;

const GameIcon = styled(SportsEsports)`
  width: 75%;
  height: 75%;
`;

function SelectApp() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("");
  const navigate = useNavigate();
  const { selectApp } = useAuth();

  const getApps = useCallback(async () => {
    try {
      setLoading(true);

      const {
        data: { entries },
      } = await axios.get(`/api/v1/games`);

      setApps(entries);
    } catch (error) {
      setError(error.messages);
      console.log(error);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    getApps();
  }, [getApps]);

  if (loading) {
    return <CircularIndeterminate />;
  }

  if (error) {
    return (
      <Alert mb={4} severity="error">
        {error}
      </Alert>
    );
  }

  const filteredApps = apps.filter((app) =>
    app.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <React.Fragment>
      <Helmet title="Select App" />
      <Wrapper elevation={1}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Typography component="h1" variant="h4" align="center" gutterBottom>
              Select An App
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              type="text"
              name="filter"
              label="Search"
              value={filter}
              fullWidth
              onChange={(event) => setFilter(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={3} justifyContent="space-between">
              {filteredApps.map((app) => (
                <Grid item key={app.id}>
                  <Centered
                    onClick={() => {
                      selectApp(app);
                      navigate("/users");
                    }}
                  >
                    <Avatar alt={app.name} src={app.logo}>
                      <GameIcon />
                    </Avatar>
                    <Typography variant="body" component="div" gutterBottom>
                      <Box fontWeight="fontWeightMedium">{app.name}</Box>
                    </Typography>
                  </Centered>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Wrapper>
    </React.Fragment>
  );
}

export default SelectApp;
